/**
 * The dashboard's own shapes — what a read produces once it has been COUNTED.
 *
 * ---------------------------------------------------------------------------
 * WHY THESE LIVE IN THEIR OWN FILE
 * ---------------------------------------------------------------------------
 * A summary is a VALUE: the numbers the dashboard shows, already counted. The reads that
 * produce them are in `api.ts`. Splitting them keeps each file about one thing — and the
 * counting rules that decide what a number MEANS (a threshold, a purpose, a quantity that is
 * not a number) sit together with the shapes they shape, rather than a screen away.
 *
 * ---------------------------------------------------------------------------
 * A COUNT IS NEVER INVENTED
 * ---------------------------------------------------------------------------
 * Every number here is COUNTED FROM A READ. Nothing is estimated, nothing is carried over from
 * a previous session, and nothing is defaulted to zero because a read failed — a failed read
 * THROWS, and the widget shows the error state. A dashboard that shows "0" for something it
 * could not read is worse than one that says it does not know.
 *
 * The one exception is stated openly: the LOW-STOCK THRESHOLD is the console's own policy, and
 * the widget PRINTS it, so the reader can see which rule produced the number.
 */

/**
 * The dashboard's low-stock rule.
 *
 * The PLATFORM has no reorder-point concept: nothing in the API says what "too little" is. So
 * this threshold is a CONSOLE policy, and the widget PRINTS it — a reader who sees "1 item at or
 * below 10 units" can see the rule that produced the number, and can ask for it to change.
 */
export const LOW_STOCK_THRESHOLD_UNITS = 10;

/**
 * The AUDITED PURPOSE the platform reads are made under.
 *
 * M63 records every platform read in the audit log against a purpose, and the API REFUSES the
 * read with `400 PURPOSE_REQUIRED` when the parameter is absent — so this is not decoration, it
 * is the difference between the dashboard's operations widgets answering and being refused.
 *
 * The value is the API's own vocabulary (`operations_triage`), not the console's: the console
 * states WHY it is asking, and the platform owns the list of reasons it accepts.
 */
export const OPERATIONS_PURPOSE = 'operations_triage';
/* ------------------------------------------------------------------------------------------------
 * The dashboard's own shapes.
 * ---------------------------------------------------------------------------------------------- */

/** The offer states the API can hold. The API's vocabulary, not the console's. */
export type OfferStateCounts = {
  readonly draft: number;
  readonly review: number;
  readonly published: number;
  readonly paused: number;
  readonly archived: number;
};

export type OffersSummary = {
  readonly total: number;
  readonly byState: OfferStateCounts;
  /** `draft + review` — the offers a merchant still has to act on. */
  readonly needsAttention: number;
};

/** The publication states the canonical catalogue holds. */
export type CatalogueSummary = {
  readonly total: number;
  readonly published: number;
  readonly byState: Readonly<Record<string, number>>;
  /**
   * The products, named.
   *
   * A count says how many there are; a NAME says which ones. A catalogue summary that
   * cannot name the catalogue is a number with nothing to attach it to.
   */
  readonly products: readonly CatalogueProduct[];
};

/** One canonical product, as the API states it. */
export type CatalogueProduct = {
  readonly id: string;
  /** The product's name, when the platform has one. */
  readonly name: string | null;
  /** The model code, when the product has one. */
  readonly modelCode: string | null;
  readonly state: string;
};

export type LowStockLine = {
  /** The offer the line is for — M18 keys stock by the offer id. */
  readonly itemId: string;
  /**
   * The SKU the tenant sells it under, when the actor may read the offers.
   *
   * `null` is a FACT, not a gap: it says the SKU is not this actor's to read. The item id is
   * always there, so the line is still identifiable.
   */
  readonly sku: string | null;
  readonly locationId: string;
  readonly available: number;
  readonly unitOfMeasure: string;
};

/**
 * How the stock summary is read.
 *
 * The SKU lives on the OFFER (M13) while the stock is keyed by the offer id (M18), so NAMING a
 * stock line needs a second read — one the actor may not be entitled to make. The page knows
 * which grants the actor holds, so it says so here rather than letting the widget ask the
 * server for something it would refuse.
 */
export type StockOptions = {
  /** Whether the actor may read the tenant's offers, and so whether the lines can be named. */
  readonly includeSkus?: boolean;
};

export type StockSummary = {
  /** How many stock lines the tenant holds. */
  readonly lines: number;
  /** The sum of `onHand` across every line. */
  readonly unitsOnHand: number;
  /** The lines at or below {@link LOW_STOCK_THRESHOLD_UNITS}. */
  readonly lowStock: readonly LowStockLine[];
  /** The threshold in force, carried so the widget can print it. */
  readonly lowStockThresholdUnits: number;
};

export type MovementSummary = {
  readonly id: string;
  readonly movementType: string;
  readonly quantity: number;
  readonly unitOfMeasure: string;
  readonly reason: string | null;
  readonly createdAt: string;
};

export type RecentActivitySummary = {
  readonly total: number;
  /** The newest movements first. */
  readonly movements: readonly MovementSummary[];
};

export type MediaSummary = {
  readonly total: number;
  readonly byStatus: Readonly<Record<string, number>>;
  /** `scan_failed + upload_expired + quarantined` — the assets a merchant must deal with. */
  readonly needsAttention: number;
};

export type TeamSummary = {
  readonly total: number;
  readonly active: number;
  readonly offboarded: number;
  readonly members: readonly {
    readonly id: string;
    readonly status: string;
    readonly functionCodes: readonly string[];
    readonly skillCodes: readonly string[];
  }[];
};

export type EntitlementSummary = {
  readonly total: number;
  readonly usable: number;
  readonly entitlements: readonly {
    readonly capability: string;
    readonly scopeType: string;
    readonly state: string;
    readonly usable: boolean;
  }[];
};

export type TenantSummary = {
  readonly total: number;
  readonly byStatus: Readonly<Record<string, number>>;
  /** Tenants with no legal entity recorded. Labelled exactly that — not "needs setup". */
  readonly withoutLegalEntity: number;
  readonly tenants: readonly {
    readonly id: string;
    readonly slug: string;
    readonly status: string;
    readonly legalEntityId: string | null;
    readonly createdAt: string;
  }[];
};
/* ------------------------------------------------------------------------------------------------
 * Helpers.
 * ---------------------------------------------------------------------------------------------- */

/**
 * Read a decimal quantity the API sent as a STRING.
 *
 * `onHand`, `reserved`, `available` and `quantity` are decimal strings on the wire so a
 * fractional quantity survives the round trip. A KPI needs a NUMBER, so the string is parsed
 * here — and a value that is not a number RAISES rather than becoming `NaN`.
 *
 * `NaN` is the failure this exists to prevent: it renders as "NaN" in a KPI, compares false
 * against every threshold, and sorts unpredictably. A dashboard that shows `NaN` is lying about
 * its own data, and a reader cannot tell it apart from a real figure.
 */
export function parseQuantity(raw: string): number {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    throw new Error(`The API returned a quantity that is not a number: "${raw}"`);
  }
  return parsed;
}

/**
 * A fresh, MUTABLE set of counters.
 *
 * The summary type is readonly, because a summary is a value and not a place to keep counting.
 * The counter this returns is the opposite: it is built here and filled in the loop below.
 */
export function emptyOfferStates(): { -readonly [K in keyof OfferStateCounts]: number } {
  return { draft: 0, review: 0, published: 0, paused: 0, archived: 0 };
}
